import { useState } from "react";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { SEOWrapper } from "@/components/SEOWrapper";
import { getSEOTemplate } from "@/data/seoTemplates";
import { usePageSeo } from "@/hooks/usePageSeo";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { ArrowRight } from "lucide-react";
import { RocSignalLabel } from "@/components/roc/RocSignalLabel";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { ModernCaptcha } from "@/components/ModernCaptcha";
import { esRoutes, enRoutes } from "@/utils/routes";

const ContactPage = () => {
  const { language, url, esUrl, enUrl, onLanguageChange } = usePageSeo();
  const routes = language === "es" ? esRoutes : enRoutes;

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    service: '',
    message: ''
  });

  const content = {
    es: {
      title: "Contacto - Luis ROC | Consultor SEO",
      description: "Contacta con Luis ROC para proyectos de SEO técnico, internacional, bilingüe y ecommerce.",
      pageTitle: "Contacta a Luis ROC",
      subtitle: "Cuéntame sobre tu sitio, tu mercado y el problema de SEO que quieres resolver.",
      formTitle: "Cuéntame sobre tu proyecto",
      nameLabel: "Nombre completo",
      namePlaceholder: "Tu nombre",
      emailLabel: "Email",
      emailPlaceholder: "tu@email.com",
      phoneLabel: "Teléfono (opcional)",
      phonePlaceholder: "Tu número de teléfono",
      serviceLabel: "Servicio de interés",
      servicePlaceholder: "Selecciona un servicio",
      messageLabel: "Mensaje",
      messagePlaceholder: "Describe tu sitio, tu mercado objetivo, el reto de SEO y qué te gustaría lograr...",
      sendButton: "Enviar Mensaje",
      sending: "Enviando...",
      contactInfo: "Información de Contacto",
      services: {
        seo_audit: "Diagnóstico SEO",
        seo_consulting: "Consultoría SEO",
        technical_seo: "SEO Técnico",
        international_seo: "SEO Internacional",
        hispanic_seo: "SEO Bilingüe / Mercado Hispano",
        ecommerce_seo: "SEO para Ecommerce",
        amazon_consulting: "Consultoría Amazon",
        llm_positioning: "Posicionamiento en IA (LLM)",
        other: "Otro"
      },
      afterTitle: "Qué pasa después de enviar tu mensaje",
      afterBody: "Luis revisa personalmente cada mensaje. Si el proyecto encaja con su forma de trabajar, te responderá por email para entender mejor el contexto y, si tiene sentido, proponer una llamada o un diagnóstico inicial. No hay un equipo de ventas ni respuestas automáticas de seguimiento.",
      helpTitle: "Qué información ayuda a responder mejor",
      helpItems: [
        "La URL de tu sitio web y, si aplica, del sitio de la competencia que te preocupa.",
        "El mercado o mercados que atiendes (por ejemplo, EE. UU. en inglés, mercado hispano, o varios países).",
        "El objetivo de negocio detrás del proyecto (más tráfico cualificado, entrar a un mercado nuevo, corregir una caída, etc.).",
        "Si ya trabajaste con una agencia o consultor de SEO antes y qué resultado tuviste."
      ],
      fitTitle: "Para quién es esto",
      fitBody: "Trabajo principalmente con empresas B2B, marcas de ecommerce y negocios que atienden al mercado hispano de EE. UU., en inglés o en español. Si tu empresa necesita una estrategia de SEO seria y a largo plazo, este es el lugar correcto para escribir.",
      notTitle: "Qué no vas a encontrar aquí",
      notItems: [
        "No prometo posiciones garantizadas ni resultados garantizados: nadie que conozca cómo funciona Google puede prometer eso de forma honesta.",
        "No vendo paquetes de enlaces baratos ni tácticas de spam.",
        "No hay un compromiso de tiempo de respuesta fijo; los mensajes se leen y se responden personalmente en el orden en que llegan."
      ],
      exploreTitle: "Mientras tanto, puedes explorar",
      exploreLinks: [
        { to: routes.seoDiagnosis, label: "Sesión de diagnóstico SEO" },
        { to: routes.services, label: "Todos los servicios" },
        { to: routes.caseStudies, label: "Casos de éxito" }
      ]
    },
    en: {
      title: "Contact - Luis ROC | SEO Consultant",
      description: "Contact Luis ROC about technical, international, bilingual and ecommerce SEO projects.",
      pageTitle: "Contact Luis ROC",
      subtitle: "Tell me about your site, your market, and the SEO problem you're trying to solve.",
      formTitle: "Tell me about your project",
      nameLabel: "Full name",
      namePlaceholder: "Your name",
      emailLabel: "Email",
      emailPlaceholder: "your@email.com",
      phoneLabel: "Phone (optional)",
      phonePlaceholder: "Your phone number",
      serviceLabel: "Service of interest",
      servicePlaceholder: "Select a service",
      messageLabel: "Message",
      messagePlaceholder: "Describe your site, your target market, the SEO challenge, and what you'd like to achieve...",
      sendButton: "Send Message",
      sending: "Sending...",
      contactInfo: "Contact Information",
      services: {
        seo_audit: "SEO Diagnosis",
        seo_consulting: "SEO Consulting",
        technical_seo: "Technical SEO",
        international_seo: "International SEO",
        hispanic_seo: "Bilingual / Hispanic Market SEO",
        ecommerce_seo: "Ecommerce SEO",
        amazon_consulting: "Amazon Consulting",
        llm_positioning: "AI Search Visibility (LLM)",
        other: "Other"
      },
      afterTitle: "What happens after you send your message",
      afterBody: "Luis reviews every message personally. If the project fits how he works, he'll reply by email to understand the context better and, if it makes sense, suggest a call or an initial diagnosis. There's no sales team and no automated follow-up sequence.",
      helpTitle: "Information that helps him respond usefully",
      helpItems: [
        "Your website URL, and a competitor's URL if that's part of the concern.",
        "The market(s) you serve (for example, the US in English, the Hispanic market, or multiple countries).",
        "The business goal behind the project (more qualified traffic, entering a new market, fixing a traffic drop, etc.).",
        "Whether you've worked with an SEO agency or consultant before, and what happened."
      ],
      fitTitle: "Who this is a fit for",
      fitBody: "Luis works mainly with B2B companies, ecommerce brands, and businesses serving the US Hispanic market, in English or Spanish. If your company needs a serious, long-term SEO strategy, this is the right place to reach out.",
      notTitle: "What you won't find here",
      notItems: [
        "No guaranteed rankings and no guaranteed results — nobody who understands how search engines work can honestly promise that.",
        "No cheap link-building packages or spam tactics.",
        "No fixed response-time commitment; messages are read and answered personally in the order they arrive."
      ],
      exploreTitle: "In the meantime, you can explore",
      exploreLinks: [
        { to: routes.seoDiagnosis, label: "SEO diagnosis session" },
        { to: routes.services, label: "All services" },
        { to: routes.caseStudies, label: "Case studies" }
      ]
    }
  };

  const currentContent = content[language];

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [verificationToken, setVerificationToken] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.message) {
      alert(language === 'es' ? 'Por favor completa todos los campos obligatorios' : 'Please fill in all required fields');
      return;
    }

    if (!verificationToken) {
      alert(language === 'es' ? 'Por favor completa la verificación de seguridad' : 'Please complete the security check');
      return;
    }

    setIsSubmitting(true);
    
    try {
      const { data, error } = await supabase.functions.invoke('send-lead-email', {
        body: {
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          company: formData.service, // Using service as company field
          website: '',
          objective: formData.service,
          budget_level: 'B', // Default medium level
          message: formData.message,
          source_page: '/contacto',
          form_type: 'contact',
          verification_token: verificationToken
        }
      });

      if (error) throw error;

      alert(language === 'es' ? 'Mensaje enviado exitosamente. Te contactaré pronto.' : 'Message sent successfully. I\'ll contact you soon.');
      
      // Reset form
      setFormData({
        name: '',
        email: '',
        phone: '',
        service: '',
        message: ''
      });
      setVerificationToken(null);
      
    } catch (error) {
      console.error('Error submitting form:', error);
      alert(language === 'es' ? 'Error al enviar el mensaje. Inténtalo de nuevo.' : 'Error sending message. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const seoTemplate = getSEOTemplate('contact');
  
  if (!seoTemplate) {
    return <div>SEO Template not found</div>;
  }

  return (
    <SEOWrapper
      title={seoTemplate.title[language]}
      description={seoTemplate.description[language]}
      keywords={seoTemplate.keywords[language]}
      url={url}
      esUrl={esUrl}
      enUrl={enUrl}
      language={language}
      pageType="website"
      structuredData={{
        type: 'contact',
        data: {
          name: language === 'es' ? "Contacto Luis ROC" : "Contact Luis ROC",
          description: seoTemplate.description[language],
          url,
          address: {
            "@type": "PostalAddress",
            "streetAddress": "9002 Six Pines Dr, Suite 277",
            "addressLocality": "Shenandoah",
            "addressRegion": "TX",
            "postalCode": "77380",
            "addressCountry": "US"
          },
          telephone: "+1-832-521-5050",
          email: "luisroc@luisroc.com",
          contactInfo: {
            email: "luisroc@luisroc.com",
            phone: "(832) 521-5050",
            areaServed: ["Houston, TX", "Texas", "United States"]
          },
          languages: ["Spanish", "English"],
          services: [
            "SEO Consulting",
            "Technical SEO",
            "International SEO",
            "Hispanic Market SEO",
            "Ecommerce SEO",
            "Amazon Consulting",
            "AI Search Visibility"
          ]
        }
      }}
      summary={language === 'es' 
        ? "Formulario de contacto directo con Luis ROC, consultor SEO senior en Houston, para proyectos de SEO técnico, internacional, bilingüe y ecommerce."
        : "Direct contact form with Luis ROC, a senior SEO consultant in Houston, for technical, international, bilingual and ecommerce SEO projects."
      }
      keyPoints={language === 'es' ? [
        "Mensajes revisados y respondidos personalmente por Luis",
        "Especialista en SEO técnico, internacional y mercado hispano de EE. UU.",
        "Servicios bilingües (español e inglés)",
        "Consultoría remota y presencial en el área metropolitana de Houston, TX",
        "Sin garantías de posiciones ni paquetes de enlaces baratos",
        "Contacto por teléfono, email y WhatsApp"
      ] : [
        "Messages reviewed and answered personally by Luis",
        "Specialist in technical, international and US Hispanic market SEO",
        "Bilingual services (Spanish and English)",
        "Remote and on-site consulting across the Houston, TX metro area",
        "No ranking guarantees, no cheap link packages",
        "Contact via phone, email and WhatsApp"
      ]}
    >

      <div className="min-h-screen bg-background">
        <Header language={language} onLanguageChange={onLanguageChange} />
        
        <main>
          <section className="border-b border-slate-800 bg-slate-950 py-16 lg:py-24">
            <div className="container-custom">
              <RocSignalLabel index="00" tone="invert">
                {language === 'es' ? 'Entrada estratégica' : 'Strategic intake'}
              </RocSignalLabel>
              <h1 className="font-display mt-6 max-w-3xl text-3xl font-bold leading-[1.1] tracking-tight text-white lg:text-5xl">
                {currentContent.pageTitle}
              </h1>
              <p className="mt-6 max-w-2xl text-base leading-relaxed text-slate-400 lg:text-lg">
                {currentContent.subtitle}
              </p>
            </div>
          </section>

          <section className="py-14 lg:py-20">
            <div className="container-custom">
              <div className="grid gap-14 lg:grid-cols-[minmax(0,0.9fr)_minmax(0,1.1fr)] lg:gap-16">
                {/* LEFT: process + verified business information */}
                <div className="min-w-0 space-y-12">
                  <div>
                    <p className="font-mono text-[11px] uppercase tracking-[0.22em] text-brand">01</p>
                    <h2 className="font-display mt-3 text-xl font-bold text-foreground">{currentContent.afterTitle}</h2>
                    <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{currentContent.afterBody}</p>
                  </div>

                  <div>
                    <p className="font-mono text-[11px] uppercase tracking-[0.22em] text-brand">02</p>
                    <h2 className="font-display mt-3 text-xl font-bold text-foreground">{currentContent.helpTitle}</h2>
                    <ul className="mt-4 border-t border-border">
                      {currentContent.helpItems.map((item) => (
                        <li key={item} className="flex gap-4 border-b border-border py-3 text-sm leading-relaxed text-muted-foreground">
                          <span className="mt-2 h-px w-4 flex-shrink-0 bg-brand" aria-hidden="true" />
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div className="grid gap-8 sm:grid-cols-2">
                    <div>
                      <h2 className="font-mono text-[11px] uppercase tracking-[0.22em] text-muted-foreground">{currentContent.fitTitle}</h2>
                      <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{currentContent.fitBody}</p>
                    </div>
                    <div>
                      <h2 className="font-mono text-[11px] uppercase tracking-[0.22em] text-muted-foreground">{currentContent.notTitle}</h2>
                      <ul className="mt-3 space-y-2">
                        {currentContent.notItems.map((item) => (
                          <li key={item} className="flex gap-3 text-sm leading-relaxed text-muted-foreground">
                            <span className="mt-2 h-px w-3 flex-shrink-0 bg-border" aria-hidden="true" />
                            <span>{item}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>

                  <div className="border border-border p-6">
                    <p className="font-mono text-[11px] uppercase tracking-[0.22em] text-brand">
                      {currentContent.contactInfo}
                    </p>
                    <dl className="mt-5 space-y-4 text-sm">
                      <div>
                        <dt className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Email</dt>
                        <dd className="mt-1">
                          <a href="mailto:luisroc@luisroc.com" className="roc-link font-semibold text-foreground">luisroc@luisroc.com</a>
                        </dd>
                      </div>
                      <div>
                        <dt className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
                          {language === 'es' ? 'Teléfono' : 'Phone'}
                        </dt>
                        <dd className="mt-1">
                          <a href="tel:+18325215050" className="roc-link font-semibold text-foreground">+1 832 521 5050</a>
                        </dd>
                      </div>
                      <div>
                        <dt className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">WhatsApp</dt>
                        <dd className="mt-1">
                          <a
                            href="https://wa.me/18325215050"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="roc-link font-semibold text-foreground"
                          >
                            +1 832 521 5050
                          </a>
                        </dd>
                      </div>
                      <div>
                        <dt className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
                          {language === 'es' ? 'Dirección comercial' : 'Business address'}
                        </dt>
                        <dd className="mt-1 text-muted-foreground">
                          9002 Six Pines Dr, Suite 277, Shenandoah, TX 77380
                          <span className="mt-1 block">
                            {language === 'es'
                              ? 'Atención con cita previa. Área de servicio: Houston, TX y alrededores.'
                              : 'By appointment only. Service area: Houston, TX and surrounding areas.'}
                          </span>
                        </dd>
                      </div>
                    </dl>
                  </div>

                  <div>
                    <p className="font-mono text-[11px] uppercase tracking-[0.22em] text-muted-foreground">
                      {currentContent.exploreTitle}
                    </p>
                    <ul className="mt-4 border-t border-border">
                      {currentContent.exploreLinks.map((item) => (
                        <li key={item.to} className="border-b border-border">
                          <Link
                            to={item.to}
                            className="group flex items-center gap-2 py-3 text-sm font-semibold text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand"
                          >
                            {item.label}
                            <ArrowRight className="h-4 w-4 text-brand transition-transform group-hover:translate-x-1" aria-hidden="true" />
                          </Link>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>

                {/* RIGHT: strategic intake form */}
                <div className="min-w-0">
                  <div className="border border-border p-6 lg:sticky lg:top-28 lg:p-8">
                    <p className="font-mono text-[11px] uppercase tracking-[0.22em] text-brand">03</p>
                    <h2 className="font-display mt-3 text-xl font-bold text-foreground lg:text-2xl">
                      {currentContent.formTitle}
                    </h2>

                    <form onSubmit={handleSubmit} className="mt-8 space-y-6">
                      <div className="grid gap-4 md:grid-cols-2">
                        <div>
                          <label htmlFor="contact-name" className="mb-2 block font-mono text-[11px] uppercase tracking-[0.16em] text-muted-foreground">
                            {currentContent.nameLabel}
                          </label>
                          <Input
                            id="contact-name"
                            className="rounded-none"
                            placeholder={currentContent.namePlaceholder}
                            value={formData.name}
                            onChange={(e) => handleInputChange('name', e.target.value)}
                            required
                          />
                        </div>
                        <div>
                          <label htmlFor="contact-email" className="mb-2 block font-mono text-[11px] uppercase tracking-[0.16em] text-muted-foreground">
                            {currentContent.emailLabel}
                          </label>
                          <Input
                            id="contact-email"
                            type="email"
                            className="rounded-none"
                            placeholder={currentContent.emailPlaceholder}
                            value={formData.email}
                            onChange={(e) => handleInputChange('email', e.target.value)}
                            required
                          />
                        </div>
                      </div>

                      <div className="grid gap-4 md:grid-cols-2">
                        <div>
                          <label htmlFor="contact-phone" className="mb-2 block font-mono text-[11px] uppercase tracking-[0.16em] text-muted-foreground">
                            {currentContent.phoneLabel}
                          </label>
                          <Input
                            id="contact-phone"
                            type="tel"
                            className="rounded-none"
                            placeholder={currentContent.phonePlaceholder}
                            value={formData.phone}
                            onChange={(e) => handleInputChange('phone', e.target.value)}
                          />
                        </div>
                        <div>
                          <label htmlFor="contact-service" className="mb-2 block font-mono text-[11px] uppercase tracking-[0.16em] text-muted-foreground">
                            {currentContent.serviceLabel}
                          </label>
                          <Select value={formData.service} onValueChange={(value) => handleInputChange('service', value)}>
                            <SelectTrigger id="contact-service" className="rounded-none">
                              <SelectValue placeholder={currentContent.servicePlaceholder} />
                            </SelectTrigger>
                            <SelectContent>
                              {Object.entries(currentContent.services).map(([key, value]) => (
                                <SelectItem key={key} value={key}>{value}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>

                      <div>
                        <label htmlFor="contact-message" className="mb-2 block font-mono text-[11px] uppercase tracking-[0.16em] text-muted-foreground">
                          {currentContent.messageLabel}
                        </label>
                        <Textarea
                          id="contact-message"
                          className="rounded-none"
                          placeholder={currentContent.messagePlaceholder}
                          value={formData.message}
                          onChange={(e) => handleInputChange('message', e.target.value)}
                          rows={6}
                          required
                        />
                      </div>

                      <ModernCaptcha
                        onVerify={(token) => setVerificationToken(token)}
                        language={language}
                        disabled={isSubmitting}
                      />

                      <Button type="submit" className="w-full rounded-none" size="lg" disabled={isSubmitting || !verificationToken}>
                        {isSubmitting ? currentContent.sending : currentContent.sendButton}
                      </Button>
                    </form>
                  </div>
                </div>
              </div>
            </div>
          </section>
        </main>

        
        <Footer language={language} />
      </div>
    </SEOWrapper>
  );
};

export default ContactPage;
